import React, { useCallback, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";

import { useNavigate } from "react-router-dom";
import { useSecurity, useUserInfo } from "../../utils/security";
import { IconButton, Stack, Step, StepLabel, Stepper, Typography, Zoom } from "@mui/material";

import { FunctionSettingsCard, LocalizationSettingsCard, hasFunction } from "../settings";

import { TimesheetSettingsCard } from "../timesheets";
import { CurrencySettingsCard, TaxSettingsCard, TransactionTypesSettingsCard } from "../financials";
import {
    FinancesSettingsFields,
    useFinancialSettingsForm,
} from "../financials/FinancesSettingsForm";

const WelcomeStep = ({ nextStep }) => {
    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}>
            <Zoom in style={{ transitionDelay: "500ms" }}>
                <Typography variant="h2" gutterBottom>
                    Vítejte
                </Typography>
            </Zoom>
            <Zoom in style={{ transitionDelay: "2500ms" }}>
                <Typography variant="h5" gutterBottom>
                    Pojďme nyní nastavit Váš nový účet
                </Typography>
            </Zoom>
            <Zoom in style={{ transitionDelay: "4500ms" }}>
                <Box sx={{ p: 4 }}>
                    <Button variant="contained" size="large" onClick={nextStep}>
                        Pokračovat
                    </Button>
                </Box>
            </Zoom>
        </Box>
    );
};

const WizardStepLayout = ({
    steps,
    title,
    description,
    onPrevious,
    onNext,
    children,
    loading,
    active,
    withButtons = true,
    nextType,
}) => {
    return (
        <>
            <Box sx={(theme) => ({ flex: 1, display: "flex", gap: theme.spacing(8) })}>
                <Box sx={{}}>
                    <Stepper orientation="vertical">
                        {(steps || {}).map((s) => (
                            <Step key={s._id} active={s._id === active}>
                                <StepLabel>{s.title}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
                <Box
                    sx={{
                        flex: "1 1 auto",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}>
                    <Box
                        sx={{
                            flex: "0 0 auto",
                            marginBottom: 1,
                            paddingBottom: 1,
                            position: "relative",
                        }}>
                        <Typography variant="h5" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {description}
                        </Typography>
                        <Box sx={{ display: loading ? "block" : "hidden" }}>
                            <LinearProgress />
                        </Box>
                    </Box>
                    <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>{children}</Box>
                </Box>
            </Box>
            {(!!onPrevious || !!onNext || nextType === "submit") && (
                <Box
                    sx={(theme) => ({
                        flex: "0 0 auto",
                        display: "flex",
                        paddingY: theme.spacing(2),
                        alignItems: "center",
                        justifyContent: "flex-end",
                        paddingTop: theme.spacing(2),
                    })}>
                    <Stack spacing={2} direction="row">
                        {!!onPrevious && (
                            <Button onClick={onPrevious} disabled={loading}>
                                Zpět
                            </Button>
                        )}
                        {(!!onNext || nextType === "submit") && (
                            <Button
                                variant="contained"
                                onClick={onNext}
                                disabled={loading}
                                type={nextType || "button"}>
                                Pokračovat
                            </Button>
                        )}
                    </Stack>
                </Box>
            )}
        </>
    );
};

const LocalizationStep = ({
    step,
    progressSteps,
    userInfo,
    updateUserInfo,
    nextStep,
    previousStep,
    loading,
    active,
}) => {
    return (
        <WizardStepLayout
            title={step.title}
            steps={progressSteps}
            onNext={nextStep}
            onPrevious={previousStep}
            loading={loading}
            active={active}>
            <LocalizationSettingsCard />
        </WizardStepLayout>
    );
};

const FunctionsStep = ({ step, progressSteps, nextStep, previousStep, active, loading }) => {
    return (
        <WizardStepLayout
            title={step.title}
            description="Vyberte prosím funkce, které chcete využívat. Toto nastavení můžete kdykoliv později změnit."
            steps={progressSteps}
            onNext={nextStep}
            onPrevious={previousStep}
            loading={loading}
            active={active}>
            <FunctionSettingsCard />
        </WizardStepLayout>
    );
};

const TimesheetsStep = ({ step, progressSteps, nextStep, previousStep, loading, active }) => {
    return (
        <WizardStepLayout
            title={step.title}
            description="Vykazování"
            steps={progressSteps}
            onNext={nextStep}
            onPrevious={previousStep}
            loading={loading}
            active={active}>
            <TimesheetSettingsCard />
        </WizardStepLayout>
    );
};

const FinancesStep = ({ step, progressSteps, nextStep, previousStep, loading, active }) => {
    const form = useFinancialSettingsForm({ onSuccess: nextStep });
    return (
        <form onSubmit={form.handleSubmit} noValidate>
            <WizardStepLayout
                title={step.title}
                description="Nastavení financí"
                steps={progressSteps}
                nextType="submit"
                onPrevious={previousStep}
                loading={loading || form.disabled}
                active={active}>
                <Box sx={{ flex: "1 1 auto", overflow: "hidden", overflowY: "auto", minHeight: 0 }}>
                    <FinancesSettingsFields form={form} vertical />
                </Box>
            </WizardStepLayout>
        </form>
    );
};

const CurrenciesStep = ({ step, progressSteps, nextStep, previousStep, loading, active }) => {
    return (
        <WizardStepLayout
            title={step.title}
            description="Nastavení financí"
            steps={progressSteps}
            onNext={nextStep}
            onPrevious={previousStep}
            loading={loading}
            active={active}>
            <CurrencySettingsCard edit="modal" />
        </WizardStepLayout>
    );
};

const TaxStep = ({ step, progressSteps, nextStep, previousStep, loading, active }) => {
    return (
        <WizardStepLayout
            title={step.title}
            description="Nastavení financí"
            steps={progressSteps}
            onNext={nextStep}
            onPrevious={previousStep}
            loading={loading}
            active={active}>
            <TaxSettingsCard edit="modal" />
        </WizardStepLayout>
    );
};

const TransactionTypesStep = ({ step, progressSteps, nextStep, previousStep, loading, active }) => {
    return (
        <WizardStepLayout
            title={step.title}
            description="Nastavení financí"
            steps={progressSteps}
            onNext={nextStep}
            onPrevious={previousStep}
            loading={loading}
            active={active}>
            <TransactionTypesSettingsCard edit="modal" />
        </WizardStepLayout>
    );
};

const DoneStep = ({ updateUserInfo, userInfo }) => {
    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}>
            <Zoom in style={{ transitionDelay: "500ms" }}>
                <Typography variant="h2" gutterBottom>
                    Hotovo!
                </Typography>
            </Zoom>
            <Zoom in style={{ transitionDelay: "2500ms" }}>
                <Box sx={{ padding: 32 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={async () => {
                            updateUserInfo({ ...userInfo, initialized: true });
                        }}>
                        Pokračovat
                    </Button>
                </Box>
            </Zoom>
        </Box>
    );
};

const steps = [
    { render: (props) => <WelcomeStep {...props} /> },
    { title: "Místní nastavení", render: (props) => <LocalizationStep {...props} /> },
    { title: "Výběr funkcí", render: (props) => <FunctionsStep {...props} /> },
    {
        title: "Vykazování",
        render: (props) => <TimesheetsStep {...props} />,
        requires: ["timesheetsSupport"],
    },
    {
        title: "Informace o společnosti",
        render: (props) => <FinancesStep {...props} />,
        requires: ["transactionsSupport"],
    },
    /*    {
        title: "Měny",
        render: (props) => <CurrenciesStep {...props} />,
        requires: ["transactionsSupport"],
    },
  */ {
        title: "Sazby daně",
        render: (props) => <TaxStep {...props} />,
        requires: ["transactionsSupport", "transactionsTaxSupport"],
    },
    {
        title: "Typy finančních transakcí",
        render: (props) => <TransactionTypesStep {...props} />,
        requires: ["transactionsSupport"],
    },
    { render: (props) => <DoneStep {...props} /> },
].map((step, idx) => ({ ...step, _id: idx }));

export const OnboardingPage = () => {
    const security = useSecurity();
    const { userInfo, updateUserInfo, userInfoLoading: loading } = useUserInfo();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);

    const progressSteps = steps.filter((s) => !!s.title && hasFunction(userInfo, s.requires));
    const step = [steps[0], ...progressSteps][activeStep];

    //TODO - check if progress steps have been changed
    const nextStep = useCallback(() => {
        setActiveStep(activeStep + 1);
    }, [activeStep, setActiveStep]);

    const previousStep = useCallback(() => {
        setActiveStep(activeStep - 1);
    }, [activeStep, setActiveStep]);

    console.log(step, progressSteps, activeStep);

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    alignContent: "center",
                    justifyContent: "center",
                    flex: "1 1 auto",
                    overflowX: "hidden",
                    overflowY: "auto",
                }}>
                <Box
                    sx={{
                        width: 940,
                        height: 600,
                        maxWidth: "90%",
                        maxHeight: "90%",
                        display: "flex",
                        flexDirection: "column",
                    }}>
                    {step.render({
                        nextStep,
                        previousStep: !!activeStep && previousStep,
                        updateUserInfo,
                        userInfo,
                        steps,
                        progressSteps,
                        step,
                        loading,
                        active: activeStep,
                    })}
                </Box>
            </Box>
            <IconButton
                sx={(theme) => ({
                    position: "absolute",
                    right: theme.spacing(2),
                    top: theme.spacing(2),
                })}
                onClick={() => security.logout(() => navigate("/"))}
                title="Odhlásit">
                <CloseIcon />
            </IconButton>
        </>
    );
};
